import { buildMenuBreadcrumbs } from '@/layouts/breadcrumbUtils';
import { LayoutBreadcrumb } from '@/layouts/LayoutBreadcrumb';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { useIsMobileLayout } from '@/shared/hooks/useMediaQuery';
import { Button } from '@/shared/components/ui/button';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  ADMIN_ACCESS_ROLES,
  hasRoleAccess,
} from '@/shared/utils/authRoles';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { Home, Menu } from 'lucide-react';
import React, { useEffect } from 'react';

import SidebarMenu from './SidebarMenu';
import { demoSidebarMenuItems } from './sidebarMenuConfig';
import UserMenu from './UserMenu';

export const DemoLayout: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, setMobileSidebarOpen } =
    useUIStore();
  const isMobileLayout = useIsMobileLayout();
  const { isAuthenticated, isInitialized, initialize, user, logout } =
    useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  // 마스터·관리·일반 로그인 허용 (시연 API는 별도 system policy)
  const hasAccess = hasRoleAccess(user?.role, ADMIN_ACCESS_ROLES);

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized, initialize]);

  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isInitialized, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    if (hasAccess) return;
    logout();
    navigate({ to: '/' });
  }, [isInitialized, isAuthenticated, hasAccess, logout, navigate]);

  if (!isInitialized || !isAuthenticated || !hasAccess) {
    return <LoadingSpinner fullscreen tip="인증 확인 중..." />;
  }

  const breadcrumbs = buildMenuBreadcrumbs(
    { path: ROUTE_PATHS.DEMO_HOME, label: '시연', icon: Home },
    demoSidebarMenuItems,
    location.pathname,
  );

  return (
    <div className="admin-layout flex h-dvh overflow-hidden">
      <SidebarMenu
        items={demoSidebarMenuItems}
        title="RUDALINK"
        subtitle="Demo"
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="ruda-header flex h-16 shrink-0 items-center justify-between gap-2 px-3 sm:px-4 xl:h-20 xl:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label={isMobileLayout ? '메뉴 열기' : '사이드바 접기/펼치기'}
              onClick={() =>
                isMobileLayout
                  ? setMobileSidebarOpen(true)
                  : setSidebarCollapsed(!sidebarCollapsed)
              }
              className="shrink-0 text-muted-foreground"
            >
              <Menu className="h-4 w-4" />
            </Button>

            <LayoutBreadcrumb segments={breadcrumbs} />
          </div>

          <UserMenu />
        </header>

        <div className="ruda-admin-container flex-1 overflow-y-auto overflow-x-hidden">
          <main className="ruda-content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};
